/**
 * Manager para el sistema de líneas de referencia dual
 * Permite alternar entre implementación personalizada y CornerstoneTools
 */

import * as cornerstoneTools from 'cornerstone-tools'
import { 
  REFERENCE_LINES_METHODS, 
  CORNERSTONE_REFERENCE_LINES_CONFIG,
  REFERENCE_LINES_VALIDATIONS,
  DEFAULT_REFERENCE_LINES_METHOD
} from '../config/referenceLinesConfig'
import ReferenceLinesDiagnostic from '../utils/ReferenceLinesDiagnostic'

class ReferenceLinesManager {
  constructor() {
    this.currentMethod = DEFAULT_REFERENCE_LINES_METHOD
    this.synchronizer = null
    this.elements = []
    this.isInitialized = false
    this.isEnabled = false
    this.logging = REFERENCE_LINES_VALIDATIONS.enableLogging
  }

  // Logging helper
  log(message, ...args) {
    if (this.logging) {
      console.log(`[ReferenceLinesManager] ${message}`, ...args)
    }
  }

  error(message, ...args) {
    console.error(`[ReferenceLinesManager] ${message}`, ...args)
  }

  // Inicializar CornerstoneTools Reference Lines
  initializeCornerstoneTools() {
    if (this.isInitialized) {
      this.log('CornerstoneTools ya está inicializado')
      return true
    }

    try {
      // Intentar usar la instancia global primero, luego la importación local
      let csTools = null
      
      // Probar diferentes formas de acceso a CornerstoneTools
      if (window.cornerstoneTools) {
        // Forma 1: Acceso directo
        if (typeof window.cornerstoneTools.getTool === 'function') {
          this.log('Usando CornerstoneTools global (acceso directo)')
          csTools = window.cornerstoneTools
        }
        // Forma 2: Acceso a través de default (módulos ES6)
        else if (window.cornerstoneTools.default && typeof window.cornerstoneTools.default.getTool === 'function') {
          this.log('Usando CornerstoneTools global (acceso a través de default)')
          csTools = window.cornerstoneTools.default
        }
        // Forma 3: Acceso a través de tools
        else if (window.cornerstoneTools.tools && typeof window.cornerstoneTools.tools.getTool === 'function') {
          this.log('Usando CornerstoneTools global (acceso a través de tools)')
          csTools = window.cornerstoneTools.tools
        }
        // Forma 4: Verificar si es un objeto con propiedades anidadas
        else {
          this.log('CornerstoneTools global existe pero no tiene la estructura esperada')
          this.log('Estructura disponible:', Object.keys(window.cornerstoneTools))
          // Intentar usar la importación local como fallback
          if (cornerstoneTools && typeof cornerstoneTools.getTool === 'function') {
            this.log('Usando CornerstoneTools importado localmente como fallback')
            csTools = cornerstoneTools
          } else {
            this.log('CornerstoneTools no está disponible en ninguna forma')
            return false
          }
        }
      } else if (cornerstoneTools && typeof cornerstoneTools.getTool === 'function') {
        this.log('Usando CornerstoneTools importado localmente')
        csTools = cornerstoneTools
      } else {
        this.log('CornerstoneTools no está disponible ni globalmente ni localmente')
        return false
      }

      // Verificar que los métodos necesarios estén disponibles
      if (!csTools.getTool || typeof csTools.getTool !== 'function') {
        this.log('CornerstoneTools no tiene el método getTool disponible')
        return false
      }

      if (!csTools.addTool || typeof csTools.addTool !== 'function') {
        this.log('CornerstoneTools no tiene el método addTool disponible')
        return false
      }

      // Inicializar CornerstoneTools si no está inicializado
      if (csTools.init && typeof csTools.init === 'function') {
        try {
          csTools.init({
            globalToolSyncEnabled: true
          })
          this.log('CornerstoneTools inicializado con globalToolSyncEnabled')
        } catch (initError) {
          this.log('Error inicializando CornerstoneTools:', initError.message)
        }
      }

      // Crear el sincronizador si está disponible
      if (csTools.Synchronizer && typeof csTools.Synchronizer === 'function') {
        this.synchronizer = new csTools.Synchronizer(
          'cornerstonenewimage',
          csTools.updateImageSynchronizer
        )
        this.log('Sincronizador creado exitosamente')
      } else {
        this.log('Synchronizer no disponible, continuando sin sincronización')
        return false
      }

      // Verificar si la herramienta ReferenceLines existe
      let referenceLinesTool
      try {
        referenceLinesTool = csTools.getTool('ReferenceLines')
      } catch (error) {
        this.log('Error obteniendo herramienta ReferenceLines:', error.message)
        return false
      }

      if (!referenceLinesTool) {
        this.log('Agregando herramienta ReferenceLines a CornerstoneTools')
        try {
          if (csTools.ReferenceLinesTool) {
            csTools.addTool(csTools.ReferenceLinesTool)
            this.log('Herramienta ReferenceLines agregada exitosamente')
          } else {
            this.log('ReferenceLinesTool no está disponible para agregar')
            return false
          }
        } catch (error) {
          this.log('Error agregando herramienta ReferenceLines:', error.message)
          return false
        }
      } else {
        this.log('Herramienta ReferenceLines ya existe')
      }

      this.isInitialized = true
      this.log('CornerstoneTools inicializado exitosamente')
      return true
    } catch (error) {
      this.error('Error inicializando CornerstoneTools:', error)
      return false
    }
  }

  // Cambiar método de líneas de referencia
  setMethod(method) {
    const forced = REFERENCE_LINES_METHODS.CORNERSTONE_TOOLS
    if (this.currentMethod === forced && method === forced) {
      this.log(`Método ${forced} ya está activo`)
      return
    }

    this.log(`Forzando CornerstoneTools (solicitado: ${method})`)
    this.deactivateCurrentMethod()
    this.currentMethod = forced
    this.activateMethod(forced)
  }

  // Activar método específico
  activateMethod(method) {
    // Solo CornerstoneTools (método personalizado desactivado)
    this.activateCornerstoneTools()
  }

  // Desactivar método actual
  deactivateCurrentMethod() {
    this.deactivateCornerstoneTools()
  }

  // Activar CornerstoneTools
  activateCornerstoneTools() {
    if (!this.initializeCornerstoneTools()) {
      this.log('No se pudo inicializar CornerstoneTools ReferenceLines')
      return false
    }
    
    if (this.synchronizer && this.elements.length >= REFERENCE_LINES_VALIDATIONS.minViewports) {
      try {
        // Obtener la instancia de CornerstoneTools que se usó en la inicialización
        let csTools = null
        if (window.cornerstoneTools && typeof window.cornerstoneTools.getTool === 'function') {
          csTools = window.cornerstoneTools
        } else if (window.cornerstoneTools && window.cornerstoneTools.default && typeof window.cornerstoneTools.default.getTool === 'function') {
          csTools = window.cornerstoneTools.default
        } else if (cornerstoneTools && typeof cornerstoneTools.getTool === 'function') {
          csTools = cornerstoneTools
        }

        if (!csTools) {
          this.log('No se pudo obtener la instancia de CornerstoneTools')
          return false
        }

        // Agregar todos los elementos al sincronizador ANTES de habilitar la herramienta
        this.elements.forEach(element => {
          try {
            this.synchronizer.add(element)
            this.log(`Elemento agregado al sincronizador: ${element.id || 'sin-id'}`)
          } catch (error) {
            this.log(`Error agregando elemento al sincronizador: ${error.message}`)
          }
        })

        // Habilitar la herramienta ReferenceLines con el contexto de sincronización
        csTools.setToolEnabled(CORNERSTONE_REFERENCE_LINES_CONFIG.toolName, {
          synchronizationContext: this.synchronizer,
        })
        
        this.isEnabled = true
        this.log('CornerstoneTools ReferenceLines activado exitosamente')
        return true
      } catch (error) {
        this.error('Error activando CornerstoneTools:', error)
        return false
      }
    } else {
      this.log('Esperando más elementos para activar CornerstoneTools')
      return false
    }
  }

  // Desactivar CornerstoneTools
  deactivateCornerstoneTools() {
    if (this.synchronizer) {
      try {
        cornerstoneTools.setToolDisabled(CORNERSTONE_REFERENCE_LINES_CONFIG.toolName)
        this.isEnabled = false
        this.log('CornerstoneTools ReferenceLines desactivado')
      } catch (error) {
        this.error('Error desactivando CornerstoneTools:', error)
      }
    }
  }

  // Activar método personalizado
  activateCustom() {
    this.log('Método personalizado activado')
    this.isEnabled = true
    // La lógica personalizada se maneja en App.js y DicomViewer.js
  }

  // Desactivar método personalizado
  deactivateCustom() {
    this.log('Método personalizado desactivado')
    this.isEnabled = false
    // La lógica personalizada se maneja en App.js y DicomViewer.js
  }

  // Habilitar líneas de referencia
  enable() {
    this.isEnabled = true
    this.log('Líneas de referencia habilitadas')
    
    if (this.currentMethod === REFERENCE_LINES_METHODS.CORNERSTONE_TOOLS) {
      return this.activateCornerstoneTools()
    }
    return true
  }

  // Deshabilitar líneas de referencia
  disable() {
    this.isEnabled = false
    this.log('Líneas de referencia deshabilitadas')
    
    if (this.currentMethod === REFERENCE_LINES_METHODS.CORNERSTONE_TOOLS) {
      this.deactivateCornerstoneTools()
    }
  }

  // Agregar elemento al sincronizador
  addElement(element) {
    if (!element) {
      this.error('Elemento no válido para agregar al sincronizador')
      return false
    }

    // Verificar si el elemento está habilitado por Cornerstone
    try {
      const enabledElement = window.cornerstone && window.cornerstone.getEnabledElement(element)
      if (!enabledElement) {
        this.log(`Elemento no está habilitado por Cornerstone, omitiendo...`)
        return false
      }
    } catch (error) {
      this.log(`Elemento no está habilitado por Cornerstone: ${error.message}`)
      return false
    }

    // Si no hay sincronizador, intentar inicializar CornerstoneTools primero
    if (!this.synchronizer) {
      this.log('No hay sincronizador, intentando inicializar CornerstoneTools...')
      if (!this.initializeCornerstoneTools()) {
        this.error('No se pudo inicializar CornerstoneTools para agregar elemento')
        return false
      }
    }

    // Verificar si el elemento ya está registrado
    if (this.elements.includes(element)) {
      this.log(`Elemento ya está registrado. Total: ${this.elements.length}`)
      return true
    }

    // Agregar elemento al sincronizador si existe
    if (this.synchronizer) {
      try {
        this.synchronizer.add(element)
        this.elements.push(element)
        this.log(`Elemento agregado al sincronizador. Total: ${this.elements.length}`)
        
        // Si tenemos suficientes elementos y estamos usando CornerstoneTools, activar
        if (this.currentMethod === REFERENCE_LINES_METHODS.CORNERSTONE_TOOLS && 
            this.elements.length >= REFERENCE_LINES_VALIDATIONS.minViewports) {
          this.log(`Suficientes elementos (${this.elements.length}), activando CornerstoneTools...`)
          this.activateCornerstoneTools()
        }
        return true
      } catch (error) {
        this.error('Error agregando elemento al sincronizador:', error)
        return false
      }
    } else {
      this.error('Sincronizador no disponible para agregar elemento')
      return false
    }
  }

  // Remover elemento del sincronizador
  removeElement(element) {
    if (!element) {
      this.error('Elemento no válido para remover del sincronizador')
      return false
    }

    if (this.synchronizer && this.elements.includes(element)) {
      try {
        this.synchronizer.remove(element)
        this.elements = this.elements.filter(el => el !== element)
        this.log(`Elemento removido del sincronizador. Total: ${this.elements.length}`)
        
        // Si no tenemos suficientes elementos, desactivar CornerstoneTools
        if (this.elements.length < REFERENCE_LINES_VALIDATIONS.minViewports) {
          this.deactivateCornerstoneTools()
        }
        return true
      } catch (error) {
        this.error('Error removiendo elemento del sincronizador:', error)
        return false
      }
    }
    return false
  }

  // Limpiar todos los elementos
  clearElements() {
    if (this.synchronizer) {
      this.elements.forEach(element => {
        try {
          this.synchronizer.remove(element)
        } catch (error) {
          this.error('Error removiendo elemento:', error)
        }
      })
      this.elements = []
      this.log('Todos los elementos removidos del sincronizador')
    }
  }

  // Obtener método actual
  getCurrentMethod() {
    return this.currentMethod
  }

  // Verificar si está usando CornerstoneTools
  isUsingCornerstoneTools() {
    return this.currentMethod === REFERENCE_LINES_METHODS.CORNERSTONE_TOOLS
  }

  // Verificar si está usando método personalizado
  isUsingCustom() {
    return this.currentMethod === REFERENCE_LINES_METHODS.CUSTOM
  }

  // Método de diagnóstico para verificar el estado actual
  getDiagnosticInfo() {
    const info = {
      currentMethod: this.currentMethod,
      isUsingCornerstoneTools: this.isUsingCornerstoneTools(),
      isUsingCustom: this.isUsingCustom(),
      isInitialized: this.isInitialized,
      isEnabled: this.isEnabled,
      elementsCount: this.elements.length,
      synchronizerExists: !!this.synchronizer,
      cornerstoneToolsAvailable: !!window.cornerstoneTools,
      loggingEnabled: this.logging
    }

    // Información adicional sobre CornerstoneTools si está disponible
    if (window.cornerstoneTools) {
      try {
        const referenceLinesTool = window.cornerstoneTools.getTool('ReferenceLines')
        info.cornerstoneReferenceLinesToolExists = !!referenceLinesTool
        info.cornerstoneReferenceLinesToolEnabled = referenceLinesTool ? 
          window.cornerstoneTools.getToolEnabled('ReferenceLines') : false
      } catch (error) {
        info.cornerstoneToolsError = error.message
      }
    }

    return info
  }

  // Método para imprimir información de diagnóstico en la consola
  printDiagnosticInfo() {
    const info = this.getDiagnosticInfo()
    console.group('🔍 ReferenceLinesManager - Estado Actual')
    console.log('📋 Método actual:', info.currentMethod)
    console.log('🔧 Usando CornerstoneTools:', info.isUsingCornerstoneTools)
    console.log('🛠️ Usando método personalizado:', info.isUsingCustom)
    console.log('✅ Inicializado:', info.isInitialized)
    console.log('🎯 Habilitado:', info.isEnabled)
    console.log('📊 Elementos registrados:', info.elementsCount)
    console.log('🔄 Sincronizador existe:', info.synchronizerExists)
    console.log('📦 CornerstoneTools disponible:', info.cornerstoneToolsAvailable)
    console.log('📝 Logging habilitado:', info.loggingEnabled)
    
    if (info.cornerstoneToolsAvailable) {
      console.log('🔧 Herramienta ReferenceLines existe:', info.cornerstoneReferenceLinesToolExists)
      console.log('🎯 Herramienta ReferenceLines habilitada:', info.cornerstoneReferenceLinesToolEnabled)
    }
    
    if (info.cornerstoneToolsError) {
      console.error('❌ Error en CornerstoneTools:', info.cornerstoneToolsError)
    }
    
    console.groupEnd()
    return info
  }

  // Verificar si está habilitado
  isEnabled() {
    return this.isEnabled
  }

  // Obtener número de elementos
  getElementCount() {
    return this.elements.length
  }

  // Verificar si tiene suficientes elementos
  hasEnoughElements() {
    return this.elements.length >= REFERENCE_LINES_VALIDATIONS.minViewports
  }

  // Obtener estado completo
  getStatus() {
    return {
      currentMethod: this.currentMethod,
      isEnabled: this.isEnabled,
      isInitialized: this.isInitialized,
      elementCount: this.elements.length,
      hasEnoughElements: this.hasEnoughElements(),
      synchronizer: !!this.synchronizer
    }
  }

  // Configurar logging
  setLogging(enabled) {
    this.logging = enabled
    this.log(`Logging ${enabled ? 'habilitado' : 'deshabilitado'}`)
  }
}

// Exportar las constantes necesarias
export { 
  REFERENCE_LINES_METHODS, 
  CORNERSTONE_REFERENCE_LINES_CONFIG,
  REFERENCE_LINES_VALIDATIONS 
} from '../config/referenceLinesConfig'

// Crear instancia global
const referenceLinesManager = new ReferenceLinesManager()

  // Exponer métodos de diagnóstico globalmente para facilitar el debugging
  if (typeof window !== 'undefined') {
    window.ReferenceLinesDiagnostic = {
      // Verificar estado actual
      getStatus: () => referenceLinesManager.getDiagnosticInfo(),
      
      // Imprimir información completa en consola
      printStatus: () => referenceLinesManager.printDiagnosticInfo(),
      
      // Verificar si está usando CornerstoneTools
      isUsingCornerstoneTools: () => referenceLinesManager.isUsingCornerstoneTools(),
      
      // Verificar si está usando método personalizado
      isUsingCustom: () => referenceLinesManager.isUsingCustom(),
      
      // Cambiar método (para testing)
      setMethod: (method) => referenceLinesManager.setMethod(method),
      
      // Habilitar/deshabilitar logging
      enableLogging: () => { referenceLinesManager.logging = true },
      disableLogging: () => { referenceLinesManager.logging = false },
      
      // Obtener información detallada de elementos
      getElementsInfo: () => {
        const info = referenceLinesManager.getDiagnosticInfo()
        console.group('📊 Elementos Registrados')
        console.log('Total de elementos:', info.elementsCount)
        referenceLinesManager.elements.forEach((element, index) => {
          console.log(`Elemento ${index}:`, element)
        })
        console.groupEnd()
        return referenceLinesManager.elements
      },

      // Análisis avanzado usando el nuevo sistema de diagnóstico
      getCompleteAnalysis: () => ReferenceLinesDiagnostic.getCompleteAnalysis(),
      printCompleteAnalysis: () => ReferenceLinesDiagnostic.printCompleteAnalysis(),
      
      // Verificaciones específicas
      checkCornerstoneTools: () => ReferenceLinesDiagnostic.checkCornerstoneToolsAvailability(),
      checkCustomMethod: () => ReferenceLinesDiagnostic.checkCustomMethod(),
      
      // Monitoreo en tiempo real
      startMonitoring: () => ReferenceLinesDiagnostic.startDOMMonitoring(),
      stopMonitoring: () => ReferenceLinesDiagnostic.cleanup(),
      
      // Métricas de rendimiento
      getMetrics: () => ReferenceLinesDiagnostic.performanceMetrics,
      getHistory: () => ReferenceLinesDiagnostic.methodHistory
    }
    
    // Mensaje de ayuda
    console.log('🔧 ReferenceLinesDiagnostic avanzado disponible. Usa ReferenceLinesDiagnostic.printCompleteAnalysis() para análisis completo.')
  }

export default referenceLinesManager
